import React from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { Info } from 'lucide-react';

/** An info icon beside a label; the note shows on hover or focus. */
export const FieldHint: React.FC<{ text: string }> = ({ text }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type='button'
          aria-label={text}
          className='inline-flex rounded text-muted-foreground hover:text-foreground'
        >
          <Info className='h-3.5 w-3.5' />
        </button>
      </TooltipTrigger>
      <TooltipContent>{text}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

interface SectionProps {
  title: string;
  /** A standing fact about these fields, said on the header rather than left
   *  behind the icon beside it. */
  note?: string;
  /** The longer version, behind the icon. */
  hint?: string;
  /** Buttons at the right of the header. */
  actions?: React.ReactNode;
  children: React.ReactNode;
}

/** A bordered block with a shaded header, so each part of the dialog reads as its own. */
export const DemoFormSection: React.FC<SectionProps> = ({
  title,
  note,
  hint,
  actions,
  children,
}) => (
  <section className='rounded-md border'>
    <header className='flex flex-col gap-2 border-b bg-muted/50 px-3 py-2 sm:flex-row sm:items-center sm:justify-between'>
      <h3 className='flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm font-semibold'>
        {title}
        {(note || hint) && (
          <span className='flex items-center gap-1 text-xs font-normal text-muted-foreground'>
            {note}
            {hint && <FieldHint text={hint} />}
          </span>
        )}
      </h3>
      {actions && <div className='flex flex-wrap gap-2'>{actions}</div>}
    </header>
    <div className='space-y-2 p-3'>{children}</div>
  </section>
);

interface FieldProps {
  label: React.ReactNode;
  error?: string;
  className?: string;
  /**
   * Render a div, not a label: for a field whose header holds a button (the
   * Kinnser mark), which a label would take as its control. The input then
   * needs its own aria-label.
   */
  unlabelled?: boolean;
  children: React.ReactNode;
}

/** A compact label-over-input field. */
export const DemoField: React.FC<FieldProps> = ({
  label,
  error,
  className,
  unlabelled,
  children,
}) => {
  const Wrapper = unlabelled ? 'div' : 'label';
  return (
    <Wrapper className={`flex min-w-0 flex-col gap-1 ${className ?? ''}`}>
      <span className='flex items-center gap-1.5 text-xs font-medium'>
        {label}
      </span>
      {children}
      {error && <span className='text-xs text-red-700'>{error}</span>}
    </Wrapper>
  );
};
